<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import TreeViewCategoria from '@/components/Financeiro/cadastrosConfiguracoes/categoriasFinanceiras/TreeViewCategoria.vue'
import ReportItemCategoria from '@/components/Financeiro/cadastrosConfiguracoes/categoriasFinanceiras/ReportItemCategoria.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'
import { ST } from '@/constants/ST'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  },
  modeloVazio: {
    type: Object,
    default: () => ({
      idPlanoCategoriaFinanceira: 0,
      nome: '',
      descricao: '',
      mascaraFormatacao: '0.0.00.00',
      dataInicioVigencia: new Date().toISOString().split('T')[0],
      dataFimVigencia: '9999-12-31',
      lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Rascunho,
      isTemplateSistema: false,
      idOrganizacao: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const temPermissaoCompleta = computed(() => authStore.pode('CATEGORIAS_FINANCEIRAS_COMPLETO'))
const isReadOnly = computed(() => {
  return isEditing.value && !temPermissaoCompleta.value
})

// Plano em Rascunho permite exclusão de categorias
const planoEhRascunho = computed(() => {
  return item.value.lstStatusPlano === FIN_ST_PLAN_CATEGFIN.Rascunho
})

// Filtra árvore para exibir apenas categorias Ativas no relatório
const categoriasAtivasReport = computed(() => {
  const filtrarAtivas = (cats) => {
    if (!cats) return []
    return cats
      .filter(c => c.lstStatus === ST.Ativo)
      .map(c => ({
        ...c,
        subcategorias: filtrarAtivas(c.subcategorias)
      }))
  }
  return filtrarAtivas(categoriasArvore.value)
})

// --- Auditoria ---
const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

// --- Formulário Base ---
const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

// --- Lógica de "Novo Plano" vs "Editando" ---
const isPlanoSaved = ref(isEditing.value)
const isSavingPlanoLocal = ref(false)
const isCategoriasFrameBlocked = computed(() => !isPlanoSaved.value || isReadOnly.value)

// --- Máscara commitada (salva no DB) usada pela TreeView ---
// Evita que a TreeView reaja à digitação da máscara antes de salvar
const mascaraSalva = ref('0.0.00.00')

// --- Listas ---
const isLoadingListas = ref(false)
const listas = reactive({})

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['PlanoCategoriaFinanceira', 'CategoriaFinanceira', 'CategoriaFinanceiraRegra'])
    Object.assign(listas, result)
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar opções.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

// --- Accordion State ---
const accordionState = reactive({
  plano: true,
  categorias: false,
  categoriasFrames: true,
  regras: true
})

const toggleCategoriasFrames = () => {
  accordionState.categoriasFrames = !accordionState.categoriasFrames
}

const planoSalvo = computed(() => {
  return item.value.idPlanoCategoriaFinanceira > 0
})

// --- FRAME 1: Plano Categoria Financeira ---

// Botão "Salvar Plano e Liberar Categorias" (apenas na criação)
const salvarPlanoLocalmente = async () => {
  if (isReadOnly.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  const erroVigencia = validarVigencia()
  if (erroVigencia) {
    emit('show-notification', { message: erroVigencia, type: 'error' })
    return
  }

  isSavingPlanoLocal.value = true

  const payload = {
    ...item.value
  }

  try {
    const response = await api.post('/PlanosCategoriasFinanceiras', payload)
    item.value = response.data

    // Atualiza a máscara commitada com o valor salvo no backend
    mascaraSalva.value = item.value.mascaraFormatacao

    isPlanoSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idPlanoCategoriaFinanceira}`

    emit('show-notification', { message: 'Plano salvo. Agora pode adicionar categorias.', type: 'success' })

    accordionState.categorias = true
    await carregarArvore()
  } catch (error) {
    console.error('Erro ao salvar plano:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar o plano.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingPlanoLocal.value = false
  }
}

// --- Validação de mudança de máscara ---
const encontrarMaxNivel = (arvore) => {
  let maxNivel = 0
  const percorrer = (cats) => {
    for (const cat of cats) {
      if (cat.nivel > maxNivel) maxNivel = cat.nivel
      if (cat.subcategorias && cat.subcategorias.length > 0) {
        percorrer(cat.subcategorias)
      }
    }
  }
  percorrer(arvore)
  return maxNivel
}

const validarMudancaMascara = () => {
  const novaMascara = item.value.mascaraFormatacao
  if (novaMascara === mascaraSalva.value) return null // sem mudança, ok

  const segmentosOriginais = mascaraSalva.value.split('.')
  const segmentosNovos = novaMascara.split('.')

  // Encontra o maior nível com categorias
  const maxNivelComCategoria = encontrarMaxNivel(categoriasArvore.value)

  if (maxNivelComCategoria === 0) return null // sem categorias, qualquer mudança ok

  // Valida: novos segmentos devem ter pelo menos maxNivelComCategoria
  if (segmentosNovos.length < maxNivelComCategoria) {
    return `A máscara deve ter pelo menos ${maxNivelComCategoria} nível(is) para preservar as categorias existentes.`
  }

  // Valida: primeiros maxNivelComCategoria segmentos devem ser iguais
  for (let i = 0; i < maxNivelComCategoria; i++) {
    if (segmentosOriginais[i] !== segmentosNovos[i]) {
      return `Não é possível alterar o nível ${i + 1} da máscara pois existem categorias cadastradas neste nível.`
    }
  }

  return null // válido
}

// --- Validação de vigência ---
const validarVigencia = () => {
  if (!item.value.dataInicioVigencia || !item.value.dataFimVigencia) {
    return 'As datas de início e fim de vigência são obrigatórias.'
  }
  if (item.value.dataInicioVigencia > item.value.dataFimVigencia) {
    return 'A data de início não pode ser posterior à data de fim de vigência.'
  }
  return null
}

// Botão "Salvar" principal do topo (final)
const submitFormPlano = async () => {
  if (isReadOnly.value) return

  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  // Valida vigência antes de salvar
  const erroVigencia = validarVigencia()
  if (erroVigencia) {
    emit('show-notification', { message: erroVigencia, type: 'error' })
    return
  }

  // Valida mudança de máscara antes de salvar
  const erroMascara = validarMudancaMascara()
  if (erroMascara) {
    emit('show-notification', { message: erroMascara, type: 'error' })
    return
  }

  const payload = {
    ...item.value
  }

  await submitForm('PlanosCategoriasFinanceiras', 'idPlanoCategoriaFinanceira', payload)

  // Após salvar com sucesso, atualiza a máscara commitada
  mascaraSalva.value = item.value.mascaraFormatacao
}

// --- FRAME 2: Categorias Financeiras (TreeView) ---
const categoriasArvore = ref([])
const isLoadingArvore = ref(false)

const carregarArvore = async () => {
  if (!item.value.idPlanoCategoriaFinanceira) return

  isLoadingArvore.value = true
  try {
    const response = await api.get(`/CategoriasFinanceiras/arvore/${item.value.idPlanoCategoriaFinanceira}`)
    categoriasArvore.value = response.data
  } catch (error) {
    console.error('Erro ao carregar árvore de categorias:', error)
    emit('show-notification', { message: 'Erro ao carregar categorias.', type: 'error' })
  } finally {
    isLoadingArvore.value = false
  }
}

// --- Estado de expansão centralizado (Set de IDs expandidos) ---
const expandidos = ref(new Set())
// expandidos é passado ao TreeViewCategoria via prop + evento update:expandidos

// Flag para controlar se é a primeira carga da árvore
// Na primeira carga, expande tudo; nas recargas (após salvar/excluir), preserva o estado
let isPrimeiraCargaArvore = true

const expandirTudo = () => {
  if (!categoriasArvore.value || categoriasArvore.value.length === 0) return
  const ids = new Set()
  const percorrer = (cats) => {
    for (const cat of cats) {
      if (cat.subcategorias && cat.subcategorias.length > 0) {
        ids.add(cat.idCategoriaFinanceira)
        percorrer(cat.subcategorias)
      }
    }
  }
  percorrer(categoriasArvore.value)
  expandidos.value = ids
}

const colapsarTudo = () => { expandidos.value = new Set() }

const expandirAteNivel = (nivel) => {
  if (!categoriasArvore.value || categoriasArvore.value.length === 0) return
  const ids = new Set()
  const percorrer = (cats, nivelAtual) => {
    for (const cat of cats) {
      if (nivelAtual < nivel && cat.subcategorias && cat.subcategorias.length > 0) {
        ids.add(cat.idCategoriaFinanceira)
        percorrer(cat.subcategorias, nivelAtual + 1)
      }
    }
  }
  percorrer(categoriasArvore.value, 1)
  expandidos.value = ids
}

// Expande automaticamente ao carregar a árvore (apenas na primeira carga)
watch(categoriasArvore, (novaArvore) => {
  if (novaArvore && novaArvore.length > 0) {
    if (isPrimeiraCargaArvore) {
      expandirTudo()
      isPrimeiraCargaArvore = false
    }
    // Nas recargas subsequentes, preserva o estado expandidos do usuário
  }
}, { immediate: true })

// --- Filtro de categorias por código estruturado ou nome ---
const filtroCategorias = ref('')

const categoriasFiltradas = computed(() => {
  const termo = filtroCategorias.value.toLowerCase().trim()
  if (!termo) return categoriasArvore.value

  const filtrar = (cats) => {
    return cats
      .filter(c => {
        const match = c.codigoEstruturado.toLowerCase().includes(termo) ||
                      c.nome.toLowerCase().includes(termo)
        const subsMatch = c.subcategorias && filtrar(c.subcategorias).length > 0
        return match || subsMatch
      })
      .map(c => ({
        ...c,
        subcategorias: c.subcategorias ? filtrar(c.subcategorias) : []
      }))
  }
  return filtrar(categoriasArvore.value)
})

// --- Handler de mover (drag-and-drop) ---
const onMover = async ({ itemId, destinoId }) => {
  if (!itemId || itemId === destinoId) return
  try {
    await api.put(`/CategoriasFinanceiras/${itemId}/mover`, {
      novoIdCategoriaFinanceiraPai: destinoId
    })
    emit('show-notification', { message: 'Categoria movida com sucesso.', type: 'success' })
    await carregarArvore()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao mover categoria.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Regras ---
const regras = ref([])
const carregarRegras = async () => {
  if (!item.value.idPlanoCategoriaFinanceira) return
  try {
    const res = await api.get(`/CategoriaFinanceiraRegras?idPlano=${item.value.idPlanoCategoriaFinanceira}`)
    regras.value = res.data
  } catch (e) { console.error(e) }
}

let watchPlanoLoaded = false
watch(() => item.value.idPlanoCategoriaFinanceira, (novoId) => {
  if (novoId && novoId > 0) {
    if (watchPlanoLoaded) return // Evita chamadas duplicadas
    watchPlanoLoaded = true

    isPlanoSaved.value = true
    accordionState.categorias = true
    // Atualiza a máscara commitada com o valor carregado do backend
    mascaraSalva.value = item.value.mascaraFormatacao
    carregarArvore()
    carregarRegras()
  }
}, { immediate: true })

// --- Modal de Regra ---
const isModalRegraOpen = ref(false)
const isEditingRegra = ref(false)
const regraModelo = reactive({
  idCategoriaFinanceiraRegra: 0,
  idPlanoCategoriaFinanceira: 0,
  idCategoriaFinanceira: null,
  nomeCategoriaFinanceira: '',
  codigoEstruturado: '',
  lstParametro: '',
  lstStatus: ''
})

// --- Regras: Parâmetros já usados em regras Ativas ---
const parametrosAtivosUsados = computed(() => {
  return new Set(
    regras.value
      .filter(r => r.lstStatus === ST.Ativo)
      .map(r => r.lstParametro)
  )
})

const parametrosAtivos = computed(() => {
  if (!listas.LstParametro) return []
  return listas.LstParametro.filter(p => {
    const ativo = p.Ativo !== undefined ? p.Ativo : p.ativo
    return ativo === undefined || ativo === true
  })
})

const parametrosDisponiveis = computed(() => {
  if (!listas.LstParametro) return undefined
  const usados = new Set(parametrosAtivosUsados.value)
  // Se estiver editando, permite o próprio parâmetro atual
  if (isEditingRegra.value && regraModelo.lstParametro) {
    usados.delete(regraModelo.lstParametro)
  }
  return parametrosAtivos.value.filter(p => !usados.has(String(p.idListaClienteItem)))
})

const totalParametros = computed(() => parametrosAtivos.value.length || 0)
const parametrosDefinidos = computed(() => parametrosAtivosUsados.value.size)
const parametrosFaltantes = computed(() => totalParametros.value - parametrosDefinidos.value)

// Configuração de busca de Categoria Financeira para o modal de regras
const configBuscaCategoria = computed(() => {
  if (!item.value.idPlanoCategoriaFinanceira) return null
  return criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira',
    null, // lstTipoMovimentacao - sem filtro por tipo
    ST.Ativo, // lstStatus - apenas categorias ativas
    authStore.organizacaoAtiva,
    null, // tipoTitulo - sem filtro por tipo de título
    item.value.idPlanoCategoriaFinanceira,
    null // permiteLancamento - sem filtro, mostra todas as ativas
  )
})

// --- Modal de Categoria ---
const isModalCategoriaOpen = ref(false)
const isEditingCategoria = ref(false)
const indiceCategoriaEditando = ref(-1)

const categoriaVazia = {
  idCategoriaFinanceira: 0,
  nome: '',
  lstTipoMovimentacao: '',
  lstStatus: ST.Ativo,
  permiteLancamento: false,
  nivel: 1,
  idCategoriaFinanceiraPai: null,
  idPlanoCategoriaFinanceira: 0,
  isSystem: false
}

const modalCategoria = reactive({ ...categoriaVazia })

const niveisMaximosMascara = computed(() => {
  if (!mascaraSalva.value) return 4
  return mascaraSalva.value.split('.').length
})

// Função auxiliar para buscar um nó na árvore pelo ID
const buscarNoNaArvore = (arvore, id) => {
  for (const no of arvore) {
    if (no.idCategoriaFinanceira === id) return no
    if (no.subcategorias && no.subcategorias.length > 0) {
      const encontrado = buscarNoNaArvore(no.subcategorias, id)
      if (encontrado) return encontrado
    }
  }
  return null
}

const abrirModalCriarCategoria = (paiId = null, nivel = 1, lstTipoMovimentacao = '') => {
  if (isReadOnly.value) return

  isEditingCategoria.value = false
  indiceCategoriaEditando.value = -1

  // Busca dados do pai na árvore para preencher info do modal
  let nomePai = null
  let codigoEstruturadoPai = null
  let caminhoHierarquia = null
  if (paiId) {
    const pai = buscarNoNaArvore(categoriasArvore.value, paiId)
    if (pai) {
      nomePai = pai.nome
      codigoEstruturadoPai = pai.codigoEstruturado
      caminhoHierarquia = pai.caminhoHierarquia
    }
  }

  Object.assign(modalCategoria, {
    ...categoriaVazia,
    idPlanoCategoriaFinanceira: item.value.idPlanoCategoriaFinanceira,
    idCategoriaFinanceiraPai: paiId,
    nivel: paiId ? nivel + 1 : 1,
    lstTipoMovimentacao: paiId ? lstTipoMovimentacao : '',
    nomeCategoriaPai: nomePai,
    codigoEstruturadoPai: codigoEstruturadoPai,
    caminhoHierarquia: caminhoHierarquia,
    codigoEstruturado: undefined // Importante: limpar para forçar geração no backend
  })
  isModalCategoriaOpen.value = true
}

const abrirModalEditarCategoria = async (categoria) => {
  if (isReadOnly.value) return

  try {
    const response = await api.get(`/CategoriasFinanceiras/${categoria.idCategoriaFinanceira}`)
    Object.assign(modalCategoria, response.data)
    isEditingCategoria.value = true
    isModalCategoriaOpen.value = true
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados da categoria.', type: 'error' })
  }
}

const abrirModalRegra = (regra = null) => {
  if (regra) {
    Object.assign(regraModelo, regra)
    isEditingRegra.value = true
  } else {
    Object.assign(regraModelo, {
      idCategoriaFinanceiraRegra: 0,
      idPlanoCategoriaFinanceira: item.value.idPlanoCategoriaFinanceira,
      idCategoriaFinanceira: null,
      nomeCategoriaFinanceira: '',
      codigoEstruturado: '',
      lstParametro: '',
      lstStatus: ''
    })
    isEditingRegra.value = false
  }
  isModalRegraOpen.value = true
}

const salvarRegra = async () => {
  // Validação dos campos obrigatórios antes de enviar
  if (!regraModelo.idCategoriaFinanceira) {
    emit('show-notification', { message: 'A Categoria Financeira é obrigatória.', type: 'warning' })
    return
  }
  if (!regraModelo.lstParametro) {
    emit('show-notification', { message: 'O Parâmetro é obrigatório.', type: 'warning' })
    return
  }
  if (!regraModelo.lstStatus) {
    emit('show-notification', { message: 'O Status é obrigatório.', type: 'warning' })
    return
  }

  try {
    const payload = { ...regraModelo, idPlanoCategoriaFinanceira: item.value.idPlanoCategoriaFinanceira }
    if (isEditingRegra.value) {
      await api.put(`/CategoriaFinanceiraRegras/${regraModelo.idCategoriaFinanceiraRegra}`, payload)
    } else {
      await api.post('/CategoriaFinanceiraRegras', payload)
    }
    isModalRegraOpen.value = false
    await carregarRegras()
  } catch (error) {
    const data = error.response?.data
    let msg = data?.message
    if (!msg && data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg || 'Erro ao salvar regra.', type: 'error' })
  }
}

const salvarCategoria = async () => {
  if (!modalCategoria.nome) {
    emit('show-notification', { message: 'Nome da categoria é obrigatório.', type: 'warning' })
    return
  }

  // Se não tem pai e não tem tipo de movimentação
  if (!modalCategoria.idCategoriaFinanceiraPai && !modalCategoria.lstTipoMovimentacao) {
    emit('show-notification', { message: 'Tipo de Movimentação é obrigatório para categorias de primeiro nível.', type: 'warning' })
    return
  }

  // Regra: PermiteLancamento = true não pode ter filhos
  if (modalCategoria.permiteLancamento && !isEditingCategoria.value) {
    // Verificar se o pai permite adicionar filhos
    if (modalCategoria.idCategoriaFinanceiraPai) {
      // O pai não pode ter PermiteLancamento = true (já validado no backend)
    }
  }

  try {
    const payload = {
      ...modalCategoria,
      idPlanoCategoriaFinanceira: item.value.idPlanoCategoriaFinanceira
    }

    if (isEditingCategoria.value) {
      await api.put(`/CategoriasFinanceiras/${modalCategoria.idCategoriaFinanceira}`, payload)
      emit('show-notification', { message: 'Categoria atualizada com sucesso.', type: 'success' })
    } else {
      await api.post('/CategoriasFinanceiras', payload)
      emit('show-notification', { message: 'Categoria criada com sucesso.', type: 'success' })
    }

    isModalCategoriaOpen.value = false

    // Guarda IDs que o usuário já tinha expandido antes do reload
    const idsExpandidosAntes = new Set(expandidos.value)
    const paiId = modalCategoria.idCategoriaFinanceiraPai

    await carregarArvore()

    // Restaura expandidos preservados + expande o pai se criou um novo filho
    if (!isEditingCategoria.value && paiId) {
      const novosExpandidos = new Set(idsExpandidosAntes)
      novosExpandidos.add(paiId)
      expandidos.value = novosExpandidos
    }
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao salvar categoria.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const excluirCategoria = async (categoria) => {
  if (isReadOnly.value) return
  if (!planoEhRascunho.value) {
    emit('show-notification', { message: 'Só é possível excluir categorias em planos com status Rascunho.', type: 'warning' })
    return
  }

  try {
    await api.delete(`/CategoriasFinanceiras/${categoria.idCategoriaFinanceira}`)
    emit('show-notification', { message: 'Categoria excluída com sucesso.', type: 'success' })
    await carregarArvore()
  } catch (error) {
    const msg = error.response?.data?.message || 'Erro ao excluir categoria.'
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// --- Regra: PermiteLancamento ---
// Só pode ser true se a categoria NÃO possui subcategorias
// Só pode ser alterado para false se não houver lançamentos vinculados (validado no backend)
const podeAlternarPermiteLancamento = computed(() => {
  if (modalCategoria.isSystem) return false
  // Se está editando e veio com subcategorias do backend, não pode mudar para true
  if (isEditingCategoria.value && modalCategoria.subcategorias?.length > 0) {
    return false
  }
  return true
})

// --- Helpers (usados no modal) ---
const getTextoLista = (lista, codigo) => {
  if (!lista || !codigo) return codigo || '-'
  const item = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const getTipoBadgeClass = (tipo) => {
  if (!tipo) return 'bg-secondary'
  const t = tipo.toUpperCase()
  if (t.includes('RECEITA') || t.includes('ENTRADA')) return 'bg-success'
  if (t.includes('DESPESA') || t.includes('SAÍDA') || t.includes('SAIDA')) return 'bg-danger'
  if (t.includes('CUSTO')) return 'bg-warning text-dark'
  return 'bg-info text-dark'
}

const getStatusBadgeClass = (status) => {
  if (status === ST.Inativo) return 'bg-danger-subtle text-danger border border-danger-subtle'
  return 'bg-body-tertiary text-body border border-secondary-subtle'
}

// Inicialização
onMounted(() => {
  carregarListas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Categorias Financeiras"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="submitFormPlano"
    @cancelar="handleCancel"
    @show-notification="(payload) => emit('show-notification', payload)"
    :desabilitar-salvar="isReadOnly"
    :ocultar-salvar="!isPlanoSaved"
    label-botao-adicionar="Salvar Plano"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- ============================================================ -->
    <!-- FRAME 1: PLANO DE CATEGORIAS FINANCEIRAS -->
    <!-- ============================================================ -->
    <div class="card mb-4">
      <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.plano = !accordionState.plano">
        <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-primary"></i> Plano de Categorias Financeiras</h5>
        <div>
          <span v-if="planoSalvo" class="badge bg-success text-white me-2 fs-6"><i class="bi bi-check-lg"></i> Plano #{{ item.idPlanoCategoriaFinanceira }}</span>
          <i :class="['bi', accordionState.plano ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
        </div>
      </div>
      <div class="card-body" v-show="accordionState.plano">
        <div v-if="isLoadingListas" class="alert alert-info py-2 small border-0 shadow-sm">
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          A carregar listas de opções...
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="nome"
              label="Nome do Plano"
              v-model="item.nome"
              placeholder="Ex: Plano Gerencial DRE 2026"
              maxlength="100"
              :readonly="isReadOnly"
              :required="true"
            />
          </div>

          <div class="col-md-3">
            <CampoInput
              id="mascaraFormatacao"
              label="Máscara de Formatação"
              v-model="item.mascaraFormatacao"
              placeholder="Ex: 0.0.00.00"
              maxlength="20"
              :readonly="isReadOnly"
              :required="true"
              descricao-informativa="Define a estrutura de níveis. Ex: '0.0.00.00' = 4 níveis"
            />
          </div>

          <div class="col-md-12">
            <CampoInput
              id="descricao"
              label="Descrição"
              type="textarea"
              v-model="item.descricao"
              maxlength="255"
              :readonly="isReadOnly"
            />
          </div>

          <div class="col-md-2">
            <CampoInput
              id="dataInicioVigencia"
              label="Data Início Vigência"
              type="date"
              v-model="item.dataInicioVigencia"
              :readonly="isReadOnly"
              :required="true"
            />
          </div>

          <div class="col-md-2">
            <CampoInput
              id="dataFimVigencia"
              label="Data Fim Vigência"
              type="date"
              v-model="item.dataFimVigencia"
              :readonly="isReadOnly"
              :required="true"
              descricao-informativa="Planos abertos: 31/12/9999"
            />
          </div>

          <div class="col-md-2">
            <CampoSelect
              id="lstStatusPlano"
              label="Status do Plano"
              v-model="item.lstStatusPlano"
              :options="listas.LstStatusPlano || []"
              option-value="idListaClienteItem"
              option-label="textoLista"
              :disabled="isReadOnly || isLoadingListas"
              :required="true"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar Plano e Liberar Categorias (apenas criação) -->
    <div class="mb-4" v-if="!isPlanoSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarPlanoLocalmente"
        :disabled="isSavingPlanoLocal || !item.nome || !item.mascaraFormatacao || !item.dataInicioVigencia || !item.dataFimVigencia"
      >
        <span v-if="isSavingPlanoLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Plano e Liberar Categorias
      </button>
    </div>

    <!-- ============================================================ -->
    <!-- FRAME 2: CATEGORIAS FINANCEIRAS (TREEVIEW + RELATORIO) -->
    <!-- ============================================================ -->
    <div :class="{ 'pe-none opacity-50': isCategoriasFrameBlocked }">
    <div class="row g-3 mb-4">
      <!-- COLUNA ESQUERDA: TreeView de Categorias -->
      <div class="col-md-7">
        <div class="card border-primary h-100">
          <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center cursor-pointer" @click="toggleCategoriasFrames">
            <h5 class="mb-0 text-white"><i class="bi bi-list-nested me-2"></i> Categorias Financeiras</h5>
            <div class="d-flex align-items-center gap-2">
              <span class="badge bg-light text-primary fs-6" v-if="categoriasArvore.length > 0">
                {{ categoriasArvore.length }} raiz(es)
              </span>
              <button type="button" class="btn btn-sm btn-light py-0"
                      v-if="planoSalvo && !isReadOnly"
                      @click.stop="abrirModalCriarCategoria(null, 1, '')"
                      title="Adicionar categoria raiz">
                <i class="bi bi-plus-lg"></i>
              </button>
              <i :class="['bi', accordionState.categoriasFrames ? 'bi-chevron-up' : 'bi-chevron-down', 'text-white']"></i>
            </div>
          </div>
          <div class="card-body p-0" v-show="accordionState.categoriasFrames">
            <!-- Guardião: Plano não salvo -->
            <div v-if="!planoSalvo" class="text-center text-muted py-4">
              <i class="bi bi-info-circle me-2"></i>
              Salve o plano primeiro para gerenciar as categorias financeiras.
            </div>

            <!-- Loading da árvore -->
            <div v-else-if="isLoadingArvore" class="text-center p-4">
              <div class="spinner-border text-primary spinner-border-sm me-2"></div> Carregando categorias...
            </div>

            <!-- TreeView -->
            <div v-else class="p-3">
              <div v-if="categoriasArvore.length === 0" class="text-center text-muted py-4">
                Nenhuma categoria cadastrada.
                <button type="button" class="btn btn-sm btn-outline-primary ms-3" v-if="!isReadOnly" @click="abrirModalCriarCategoria(null, 1, '')">
                  <i class="bi bi-plus-lg me-1"></i>Adicionar Categoria Raiz
                </button>
              </div>

              <!-- Filtro por código estruturado ou nome -->
              <div class="mb-2" v-if="categoriasArvore.length > 0">
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-body-tertiary border-secondary-subtle">
                    <i class="bi bi-search"></i>
                  </span>
                  <input type="text" class="form-control form-control-sm border-secondary-subtle"
                         v-model="filtroCategorias"
                         placeholder="Filtrar por código ou nome..."
                         @input="expandirTudo">
                  <button v-if="filtroCategorias" type="button"
                          class="input-group-text bg-body-tertiary border-secondary-subtle"
                          @click="filtroCategorias = ''"
                          title="Limpar filtro">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>

              <!-- Botões de expansão global -->
              <div class="d-flex gap-2 mb-2 flex-wrap" v-if="categoriasFiltradas.length > 0">
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="expandirTudo">
                  <i class="bi bi-arrows-expand me-1"></i>Expandir Tudo
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary" @click="colapsarTudo">
                  <i class="bi bi-arrows-collapse me-1"></i>Colapsar Tudo
                </button>
                <template v-if="niveisMaximosMascara > 1">
                  <button type="button" class="btn btn-sm btn-outline-secondary"
                          v-for="n in niveisMaximosMascara" :key="n"
                          @click="expandirAteNivel(n)">
                    Nível {{ n }}
                  </button>
                </template>
              </div>

              <!-- TreeView Recursivo -->
              <TreeViewCategoria
                v-if="categoriasFiltradas.length > 0"
                :categorias="categoriasFiltradas"
                :nivel="1"
                :isReadOnly="isReadOnly"
                :niveisMaximos="niveisMaximosMascara"
                :expandidos="expandidos"
                @update:expandidos="expandidos = $event"
                :listas="listas"
                :ocultarExclusao="!planoEhRascunho"
                @adicionar-filho="({ paiId, nivel, lstTipoMovimentacao }) => abrirModalCriarCategoria(paiId, nivel, lstTipoMovimentacao)"
                @editar="abrirModalEditarCategoria"
                @excluir="excluirCategoria"
                @mover="onMover"
              />

              <!-- Mensagem quando filtro não encontra resultados -->
              <div v-if="categoriasArvore.length > 0 && categoriasFiltradas.length === 0" class="text-center text-muted py-3">
                <i class="bi bi-search me-1"></i>
                Nenhuma categoria corresponde ao filtro "{{ filtroCategorias }}".
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- COLUNA DIREITA: Relatório de Categorias Ativas -->
      <div class="col-md-5">
        <div class="card border-success h-100">
          <div class="card-header bg-success text-white d-flex justify-content-between align-items-center cursor-pointer" @click="toggleCategoriasFrames">
            <h5 class="mb-0 text-white"><i class="bi bi-file-bar-graph me-2"></i> Categorias Ativas</h5>
            <i :class="['bi', accordionState.categoriasFrames ? 'bi-chevron-up' : 'bi-chevron-down', 'text-white']"></i>
          </div>
          <div class="card-body p-0" v-show="accordionState.categoriasFrames">
            <div v-if="!planoSalvo" class="text-center text-muted py-4">
              <i class="bi bi-info-circle me-2"></i>
              Salve o plano primeiro.
            </div>
            <div v-else-if="categoriasAtivasReport.length === 0" class="text-center text-muted py-4">
              Nenhuma categoria ativa encontrada.
            </div>
            <div v-else class="p-3">
              <template v-for="cat in categoriasAtivasReport" :key="cat.idCategoriaFinanceira">
                <ReportItemCategoria :categoria="cat" :listas="listas" />
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

  <!-- ============================================================ -->
  <!-- FRAME 3: REGRAS DE CATEGORIA FINANCEIRA -->
  <!-- ============================================================ -->
  <div class="card mb-4">
    <div class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center" @click="accordionState.regras = !accordionState.regras">
      <h5 class="mb-0"><i class="bi bi-gear me-2 text-primary"></i> Regras de Categoria Financeira
        <span class="badge bg-info text-dark fs-6 ms-2 align-middle" v-if="planoSalvo && totalParametros > 0">
          {{ parametrosDefinidos }}/{{ totalParametros }} parâmetros
          <span v-if="parametrosFaltantes > 0" class="ms-1">(faltam {{ parametrosFaltantes }})</span>
        </span>
      </h5>
      <div class="d-flex align-items-center gap-2">
        <button type="button" class="btn btn-sm btn-primary" @click.stop="abrirModalRegra()" v-if="planoSalvo && !isReadOnly">
          <i class="bi bi-plus-lg"></i>
        </button>
        <i :class="['bi', accordionState.regras ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
    </div>
    <div class="card-body" v-show="accordionState.regras">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>Parâmetro</th>
            <th>Categoria</th>
            <th>Tipo</th>
            <th>Caminho Completo</th>
            <th>Status</th>
            <th class="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="regra in regras" :key="regra.idCategoriaFinanceiraRegra">
            <td><span class="badge bg-secondary-subtle text-body border border-secondary-subtle">{{ getTextoLista(listas.LstParametro, regra.lstParametro) || regra.lstParametro }}</span></td>
            <td class="text-nowrap">{{ regra.codigoEstruturado }} - {{ regra.nomeCategoriaFinanceira }}</td>
            <td><span class="badge" :class="getTipoBadgeClass(regra.lstTipoMovimentacao)">{{ getTextoLista(listas.LstTipoMovimentacao, regra.lstTipoMovimentacao) || regra.lstTipoMovimentacao || '---' }}</span></td>
            <td class="small text-muted">{{ regra.caminhoHierarquia || '-' }}</td>
            <td><span class="badge" :class="getStatusBadgeClass(regra.lstStatus)">{{ getTextoLista(listas.LstStatus, regra.lstStatus) || regra.lstStatus }}</span></td>
            <td class="text-end">
              <button type="button" class="btn btn-sm btn-outline-primary" @click="abrirModalRegra(regra)"><i class="bi bi-pencil"></i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  </FormularioCadastro>

  <!-- ============================================================ -->
  <!-- MODAL: ADICIONAR/EDITAR REGRA -->
  <!-- ============================================================ -->
  <ModalFormulario
    v-model="isModalRegraOpen"
    :titulo="isEditingRegra ? 'Editar Regra' : 'Adicionar Regra'"
    largura="modal-md"
    textoConfirmar="Salvar"
    @confirmar="salvarRegra"
  >
    <div class="row g-3">
      <div class="col-md-12">
        <CampoBuscaLista
          id="lstParametro"
          label="Parâmetro"
          v-model="regraModelo.lstParametro"
          tabela="CategoriaFinanceiraRegra"
          :options="parametrosDisponiveis"
          required
        />
      </div>
      <div class="col-md-12">
        <CampoBusca
          id="idCategoriaFinanceira"
          label="Categoria Financeira"
          v-model="regraModelo.idCategoriaFinanceira"
          :configuracao-busca="configBuscaCategoria"
          required
          @selecionado="val => { regraModelo.nomeCategoriaFinanceira = val.nome; regraModelo.codigoEstruturado = val.codigoEstruturado }"
          @show-notification="p => emit('show-notification', p)"
        />
      </div>
      <div class="col-md-12">
        <CampoSelect
          id="lstStatus"
          label="Status"
          v-model="regraModelo.lstStatus"
          :options="listas.LstStatus || []"
          option-value="idListaClienteItem"
          option-label="textoLista"
          required
        />
      </div>
    </div>
  </ModalFormulario>

  <!-- ============================================================ -->
  <!-- MODAL: ADICIONAR/EDITAR CATEGORIA -->
  <!-- ============================================================ -->
  <ModalFormulario
    v-model="isModalCategoriaOpen"
    :titulo="isEditingCategoria ? ('Editar Categoria Financeira ' + (modalCategoria.codigoEstruturado || '')) : 'Adicionar Categoria Financeira'"
    largura="modal-lg"
    textoConfirmar="Salvar"
    @confirmar="salvarCategoria"
  >
    <div class="row g-3">
      <div class="col-md-8">
        <CampoInput
          id="nome"
          label="Nome da Categoria"
          v-model="modalCategoria.nome"
          placeholder="Ex: Pagamento de Fornecedores"
          maxlength="100"
          :required="true"
        />
      </div>

      <div class="col-md-4" v-if="!modalCategoria.idCategoriaFinanceiraPai">
        <CampoSelect
          id="lstTipoMovimentacao"
          label="Tipo de Movimentação"
          v-model="modalCategoria.lstTipoMovimentacao"
          :options="listas.LstTipoMovimentacao || []"
          option-value="idListaClienteItem"
          option-label="textoLista"
          :required="true"
          descricao-informativa="Apenas nós raiz definem o tipo. Filhos herdam."
        />
      </div>
      <div class="col-md-4" v-else>
        <CampoInput
          id="lstTipoMovimentacaoHeranca"
          label="Tipo de Movimentação"
          :model-value="getTextoLista(listas.LstTipoMovimentacao, modalCategoria.lstTipoMovimentacao)"
          disabled
          descricao-informativa="Herdado do nó pai"
        />
      </div>

      <div class="col-md-4">
        <CampoSelect
          id="lstStatus"
          label="Status"
          v-model="modalCategoria.lstStatus"
          :options="listas.LstStatus || []"
          option-value="idListaClienteItem"
          option-label="textoLista"
          :required="true"
        />
      </div>

      <div class="col-md-4">
        <label class="form-label">Nível na Árvore</label>
        <input type="text" class="form-control" :value="'Nível ' + (modalCategoria.nivel || 1)" disabled>
      </div>

      <div class="col-md-12">
        <div class="border rounded p-3">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" id="permiteLancamento" v-model="modalCategoria.permiteLancamento"
                   :disabled="!podeAlternarPermiteLancamento">
            <label class="form-check-label" for="permiteLancamento">
              <strong>Permite Lançamento Financeiro</strong>
            </label>
          </div>
          <small class="text-muted d-block mt-1">
            <i class="bi bi-info-circle me-1"></i>
            Se ativado, esta categoria pode receber lançamentos de títulos financeiros e movimentações bancárias.
            Categorias com lançamento ativo não podem ter subcategorias.
          </small>
          <small v-if="!podeAlternarPermiteLancamento && modalCategoria.subcategorias?.length > 0" class="text-warning d-block mt-1">
            <i class="bi bi-exclamation-triangle me-1"></i>
            Não é possível alterar pois esta categoria possui subcategorias vinculadas.
          </small>
        </div>
      </div>
    </div>

    <!-- Informação do pai (Vinculação Hierárquica) -->
    <div class="card mt-3 border-secondary" v-if="modalCategoria.idCategoriaFinanceiraPai">
      <div class="card-header bg-body-tertiary py-2">
        <h6 class="mb-0"><i class="bi bi-diagram-2 me-1 text-primary"></i> Vinculação Hierárquica</h6>
      </div>
      <div class="card-body py-2">
        <div class="mb-1">
          <span class="badge ms-2" :class="getTipoBadgeClass(modalCategoria.lstTipoMovimentacao)">
            {{ getTextoLista(listas.LstTipoMovimentacao, modalCategoria.lstTipoMovimentacao) || '---' }}
          </span>
          <span class="fw-medium small text-muted"></span>
          <span class="badge bg-light text-dark border font-monospace ms-2 me-2">
            {{ modalCategoria.codigoEstruturadoPai || '---' }}
          </span>
          <span class="small text-muted" v-if="modalCategoria.caminhoHierarquia">
            — {{ modalCategoria.caminhoHierarquia }}
          </span>
        </div>
      </div>
    </div>
  </ModalFormulario>

  <!-- ============================================================ -->
  <!-- AUDITORIA -->
  <!-- ============================================================ -->
  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="PlanoCategoriaFinanceira"
    :chave-primaria="item.idPlanoCategoriaFinanceira"
    :titulo-registro="item.nome"
    contexto="tenant"
    :dicionarios="{
      'LstStatusPlano': listas.LstStatusPlano || []
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.tree-node-container {
  border-bottom: 1px solid var(--bs-border-color);
}

.tree-node:hover {
  background-color: var(--bs-tertiary-bg);
}

.tree-node[draggable="true"] {
  cursor: grab;
}

.tree-node[draggable="true"]:active {
  cursor: grabbing;
}
</style>
